import styled from '@emotion/styled';
import { ButtonProps, Form, Input, message, Modal, ModalProps, Select } from 'antd';
import { UploadFile } from 'antd/lib/upload/interface';
import FormField from 'components/FormField';
import StatusButton from 'components/StatusButton';
import { Formik, FormikConfig, FormikProps } from 'formik';
import useModal from 'hooks/useModal';
import { ReactNode } from 'react';
import { refuse, RefuseBody, refuseReason } from 'services/api/return';
import { RefuseUploadImage, useRefuseUploadImageValidate } from './RefuseUploadImage';
import { useMemoizedFn } from 'ahooks';
import { modeTranslate } from 'services/api/overseaTranslate';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { T, useTranslate } from '@tolgee/react';
import { getApiErrorCodeMsg } from 'services/api/utils/errorCode';

const ItemWrapper = styled.div`
  display: flex;
  align-items: center;
  height: 40px;
  padding: 0 12px;
  background: #f7f9fa;
  border-radius: 2px;
  > span {
    color: var(--ant-primary-color);
    padding: 0 4px;
  }
`;

export interface RefuseFormValues
  extends Util.PartialPartial<Omit<RefuseBody, 'image'>, 'reasonId' | 'comment'> {
  image: UploadFile[];
}

type FV = RefuseFormValues;

export interface RefuseFormProps extends FormikConfig<FV> {
  internalStoreId: string;
  reasons: {
    reasonId: string;
    text: string;
  }[];
  children: (form: ReactNode, formProps: FormikProps<FV>) => ReactNode;
}

export function RefuseForm(props: RefuseFormProps) {
  const { internalStoreId, reasons, children, ...otherProps } = props;
  const { t } = useTranslate();

  const refuseUploadImageValidate = useRefuseUploadImageValidate();

  return (
    <Formik enableReinitialize {...otherProps}>
      {(formProps) => {
        const items = (
          <FormField<FV['returnOrderItemId']> name="returnOrderItemId">
            {({ field: { value } }) => {
              return (
                <ItemWrapper>
                  <T
                    keyName="ViewRefund.已选择x商品"
                    params={{
                      x: <span />,
                      count: value.length,
                    }}
                  />
                </ItemWrapper>
              );
            }}
          </FormField>
        );

        const reason = (
          <FormField<FV['reasonId']>
            name="reasonId"
            required
            label={t('trade.选择原因')}
            validate={(reasonId) => (reasonId ? undefined : t('trade.请选择原因'))}
          >
            {({ field }) => {
              return (
                <Select
                  placeholder={t('refundSetting.请选择')}
                  {...field}
                  options={reasons.map(({ reasonId, text }) => {
                    return {
                      value: reasonId,
                      label: text,
                    };
                  })}
                />
              );
            }}
          </FormField>
        );

        const imageList = (
          <FormField<FV['image']>
            name="image"
            required
            label={t('phrase.上传图片')}
            validate={refuseUploadImageValidate}
          >
            {({ field: { value, onChange } }) => {
              return (
                <RefuseUploadImage
                  internalStoreId={internalStoreId}
                  reqSource="REFUND"
                  accepts={['jpg', 'jpeg', 'png']}
                  countLimit={3}
                  sizeLimit={5}
                  value={value}
                  onChange={onChange}
                />
              );
            }}
          </FormField>
        );

        const comment = (
          <FormField<FV['comment']>
            name="comment"
            required
            label={t('ViewBlockList.filter.other.notice')}
            validate={(comment) =>
              comment
                ? undefined
                : t('department.请输入x', { x: t('ViewBlockList.filter.other.notice') })
            }
          >
            {({ field }) => {
              return (
                <Input.TextArea
                  placeholder={t('department.请输入x', {
                    x: t('ViewBlockList.filter.other.notice'),
                  })}
                  showCount
                  maxLength={200}
                  {...field}
                />
              );
            }}
          </FormField>
        );

        const form = (
          <Form layout="vertical">
            {items}
            {reason}
            {imageList}
            {comment}
          </Form>
        );

        return children(form, formProps);
      }}
    </Formik>
  );
}

export interface RefuseFormModalProps
  extends ModalProps,
    Pick<RefuseFormProps, 'internalStoreId' | 'reasons' | 'initialValues' | 'onSubmit'> {}

export function RefuseFormModal(props: RefuseFormModalProps) {
  const { internalStoreId, reasons, initialValues, onSubmit, ...otherProps } = props;

  const { t } = useTranslate();

  return (
    <RefuseForm
      internalStoreId={internalStoreId}
      reasons={reasons}
      initialValues={initialValues}
      onSubmit={onSubmit}
    >
      {(form, formProps) => {
        const { isSubmitting, handleSubmit } = formProps;

        return (
          <Modal
            title={t('ViewRefund.拒绝买家退货退款申请')}
            width={602}
            okButtonProps={{
              loading: isSubmitting,
              onClick: () => handleSubmit(),
            }}
            {...otherProps}
          >
            {form}
          </Modal>
        );
      }}
    </RefuseForm>
  );
}

export const url2File = (url: string): UploadFile => ({
  uid: url,
  name: url,
  status: 'done',
  url,
});

export interface RefuseProps extends ButtonProps {
  internalStoreId: string;
  externalStoreId: string;
  returnSn: string;
  returnOrderItemId: string[];
  onRefused?: () => void;
}

export function Refuse(props: RefuseProps) {
  const {
    internalStoreId,
    externalStoreId,
    returnSn,
    returnOrderItemId,
    onRefused,
    ...otherProps
  } = props;

  const { t } = useTranslate();

  const systemLanguage = useSystemLanguageValue();
  const { visible, show, hide, meta } = useModal<Omit<RefuseFormModalProps, 'onSubmit'>>();

  const modal = meta && (
    <RefuseFormModal
      visible={visible}
      onCancel={() => hide()}
      {...meta}
      onSubmit={(values, helpers) => {
        const { image, reasonId = '', comment = '', ...others } = values;

        return refuse({
          ...others,
          reasonId,
          comment,
          image: image
            .map((file) => file.response?.data?.url || file.url)
            .filter((_) => _) as string[],
        })
          .then(() => {
            if (onRefused) {
              onRefused();
            }

            message.success(t('ViewRefund.refundT.已拒绝'));

            hide();

            helpers.resetForm();
          })
          .catch((e) => {
            message.error(getApiErrorCodeMsg(e.response, 'ViewRefund.拒绝失败，请稍后再试'));
          });
      }}
    />
  );

  const handleRejectClick = useMemoizedFn(async () => {
    try {
      const { failReturnOrderItemId, availableReasonList } = await refuseReason({
        externalStoreId,
        returnSn,
        returnOrderItemId,
      });
      const unRefusedItemIds = returnOrderItemId.filter(
        (id) => !failReturnOrderItemId.includes(id),
      );

      if (unRefusedItemIds.length === 0) {
        message.error(t('ViewRefund.无可拒绝订单'));

        return;
      }

      if (availableReasonList.length === 0) {
        message.error(t('ViewRefund.当前已选订单的拒绝原因不一致，无法批量拒绝'));

        return;
      }

      if (unRefusedItemIds.length < returnOrderItemId.length) {
        message.warn(t('ViewRefund.已选订单中，存在不可拒绝订单'));
      }
      // 翻译拒绝原因
      const translateReasons = await modeTranslate(
        availableReasonList.map(({ text }) => text),
        systemLanguage,
        'GOOGLE',
        {
          from: 'translateTool',
        },
      );
      show({
        internalStoreId,
        reasons: availableReasonList.map((item, index) => {
          const translatedText = translateReasons[index];
          return {
            ...item,
            text: translatedText || item.text,
          };
        }),
        initialValues: {
          externalStoreId,
          returnSn,
          returnOrderItemId: unRefusedItemIds,
          image: [],
          reasonId: undefined,
          comment: '',
        },
      });
    } catch (err) {
      message.error(t('ViewRefund.获取拒绝原因列表失败，请稍后再试'));
    }
  });

  const button = (
    <StatusButton
      size="small"
      disabled={returnOrderItemId.length === 0}
      {...otherProps}
      onClick={handleRejectClick}
    >
      {t('tableColumn.拒绝')}
    </StatusButton>
  );

  return (
    <>
      {modal}
      {button}
    </>
  );
}
